import { useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import Link from 'next/link';

import { JobPosition } from '@pflegia/types/api/job-position/job-position';
import { BasePositionCardProps } from '@pflegia/types/common/interfaces/position-card';
import { FeaturesBadges } from '@pflegia/ui/shared/job-position/FeaturesBadges';
import { shouldImageBeUnoptimized } from '@pflegia/ui/shared/utils/image-unoptimized';
import { removeCountryName } from '@pflegia/ui/shared/utils/remove-country-name';
import { getPublicImageUrl } from '@pflegia/util/functions/s3/get-public-image-url';

import EmojiText from '@components/emoji-text';

import { EMPLOYERS } from '@shared/application/constants';

import defaultPerks from '../job-overview/DefaultJobPerks';

export interface WhyThisJobHeaderProps {
  jobPosition: Pick<JobPosition, 'title' | 'headline' | 'geoLocation' | 'employer'>;
  featuresBadges?: BasePositionCardProps['featuresBadges'];
}

const WhyThisJobHeader: React.FC<WhyThisJobHeaderProps> = ({ jobPosition, featuresBadges }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const employerLogo = getPublicImageUrl(jobPosition.employer.logoKey) || '';
  const address = jobPosition.geoLocation?.formattedAddress;

  return (
    <div className='-mx-6 bg-white px-4 py-6 sm:mx-0 sm:rounded-t-2xl md:pt-6 lg:px-12'>
      <div className='flex flex-1 gap-x-8'>
        {employerLogo && (
          <Image
            alt=''
            className='bg-img hidden h-24 w-24 rounded-full border-2 object-contain lg:block'
            height={96}
            src={employerLogo}
            unoptimized={shouldImageBeUnoptimized(employerLogo)}
            width={96}
          />
        )}
        <div className='text-default flex flex-col'>
          <Link
            target='_blank'
            rel='noopener noreferrer'
            href={`${EMPLOYERS}/${jobPosition.employer.id}`}
            className='text-primary mb-2 text-base font-medium underline'>
            {jobPosition.employer.name}
          </Link>
          <h1
            data-testid='job-detail-title'
            lang={language}
            className='flex-1 text-2xl font-semibold wrap-break-word hyphens-auto md:my-auto lg:block lg:text-4xl'>
            {jobPosition.headline ?? jobPosition.title}
          </h1>
          {featuresBadges && (
            <div className='mt-4 min-h-[32px]'>
              <FeaturesBadges featuresBadges={featuresBadges} />
            </div>
          )}
          {address && (
            <span className='mt-2'>
              <EmojiText emoji={defaultPerks.address} className='-ml-2'>
                {removeCountryName(address)}
              </EmojiText>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhyThisJobHeader;
